import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, MoreVertical, Archive, Trash2, AlertTriangle, Users, Crown, Check, User } from 'lucide-react';
import type { Project } from '../../types';
import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { showToast } from '../common/Toast';
import { Button, Modal } from '../common';

interface ProjectCardProps {
  project: Project;
  isSelected?: boolean;
  onToggleSelect?: (projectId: string) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, isSelected = false, onToggleSelect }) => {
  const { t } = useTranslation();
  const { archiveProject, deleteProject } = useProjectStore();
  const { openProjectModal } = useUIStore();
  const { user } = useAuthStore();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  // Check if current user is an admin of the active organization
  const isOrgAdmin = user?.organizationRoles?.find(r => r.orgId === user.activeOrganizationId)?.role === 'ADMIN';

  // Check if current user owns this project
  const isOwner = project.isOwner ?? project.ownerId === user?.id;

  // Project permissions: creator (owner) or org admin can edit/delete/archive
  const canEdit = isOwner || isOrgAdmin;
  const canDelete = isOwner || isOrgAdmin;
  const canArchive = isOwner || isOrgAdmin;

  const handleProjectClick = () => {
    // Toggle project selection for filtering
    if (onToggleSelect) {
      onToggleSelect(project.id);
    }
  };

  const handleArchive = async () => {
    setIsArchiving(true);
    await archiveProject(project.id);
    setIsArchiving(false);
    showToast(
      project.isArchived ? t('projects.card.unarchived') : t('projects.card.archived'),
      'success'
    );
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await deleteProject(project.id);
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  return (
    <>
      <div
        className={`
          group p-4 rounded-lg border transition-colors cursor-pointer relative
          ${isSelected 
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500/30' 
            : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700'
          }
        `}
        onClick={handleProjectClick}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div 
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: project.color }}
          >
            <Check className="w-4 h-4 text-white" />
          </div>
        )}

        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${project.color}20` }}
          >
            <Folder className="w-5 h-5" style={{ color: project.color }} />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                {project.name}
              </h3>
              {isOwner && (
                <span title={t('projects.card.youOwnThis')}>
                  <Crown className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                </span>
              )}
            </div>
            {/* Show creator name for non-owned projects (admins viewing others' projects) */}
            {!isOwner && project.owner && (
              <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                <User className="w-3 h-3" />
                <span>by {project.owner.firstName} {project.owner.lastName}</span>
              </div>
            )}
            {project.description && (
              <p className="text-sm text-gray-500 overflow-hidden line-clamp-2 mt-0.5">
                {project.description}
              </p>
            )}
            <div className="flex items-center gap-3 mt-1">
              <p className="text-xs text-gray-400">
                {project._count?.tasks || 0} tasks
              </p>
              {(project.memberCount ?? 0) > 0 && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <Users className="w-3.5 h-3.5" />
                  <span>{project.memberCount}</span>
                </div>
              )}
            </div>
          </div>

          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            {canArchive && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchive();
                }}
                disabled={isArchiving}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                title={project.isArchived ? t('projects.card.unarchive') : t('projects.card.archive')}
              >
                <Archive className={`w-4 h-4 ${isArchiving ? 'animate-pulse' : ''}`} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-600 transition-colors"
                title={t('projects.card.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openProjectModal(project.id);
                }}
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors"
                title={t('projects.card.edit')}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('projects.card.delete')}
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            Delete "{project.name}"?
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            This action cannot be undone. All tasks from all users within this project will be deleted.
          </p>
          <p className="text-sm text-red-600 dark:text-red-400 mb-6 font-medium">
            Se borrarán todas las tareas de todos los usuarios que estén dentro de este proyecto.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="w-full sm:w-auto">
              {t('common.cancel')}
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              isLoading={isDeleting}
              className="w-full sm:w-auto"
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ProjectCard;